import { EMPTY_VALUE } from "staticConfiguration";
import type { AlertProps, ContainerProps } from "@mui/material";
import { useBrcyCode } from "core/hooks/useBrcyCode";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useCentrixFetch } from "core/hooks/useCentrixFetch";
import { useLocale } from "core/hooks/useLocale";
import { useToaster } from "core/hooks/useToaster";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { PitstopAlertDisplay } from "./PitstopAlertDisplay";

const ONE_MINUTE_IN_MS = 60_000;

export function PitstopAlertBanner({
  alertProps,
  containerProps,
}: {
  alertProps?: AlertProps;
  containerProps?: ContainerProps;
}) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:PitstopAlertBanner",
  });
  const brcyCode = useBrcyCode();
  const locale = useLocale();
  const centrixFetch = useCentrixFetch();
  const { errorToast } = useToaster();

  const { data, mutate } = useCentrixApi({
    path: "/api/app/pitstop-alert/active-alert",
    parameters: { query: { brcyCode, locale } },
    swrOptions: { refreshInterval: ONE_MINUTE_IN_MS },
  });

  const dismissErrorMessage = t("dismissError", "Failed to dismiss alert");
  const alert = data?.value;

  const handleDismiss = useCallback(
    async (alertId: string) => {
      const res = await centrixFetch({
        method: "post",
        path: "/api/app/pitstop-alert/dismiss-alert/{alertId}",
        parameters: { path: { alertId } },
      });
      if (!res.ok) {
        errorToast(dismissErrorMessage);
        return;
      }
      await mutate();
    },
    [centrixFetch, dismissErrorMessage, errorToast, mutate]
  );

  if (!alert?.id || alert.isDismissed || !alert.alertType) {
    return null;
  }

  const alertType = alert.alertType;
  return (
    <PitstopAlertDisplay
      alertType={alertType}
      title={alert.title || EMPTY_VALUE}
      description={alert.description}
      linkHref={alert.linkHref}
      linkText={alert.linkText}
      id={alert.id}
      onClose={handleDismiss}
      alertProps={alertProps}
      containerProps={containerProps}
    />
  );
}
